import { Button, ConfirmDialog, DropdownSelect } from '@components/common';
import { usePaymentMethods } from '@hooks/accounting/usePaymentMethods';
import { useAppSelector } from '@hooks/useRedux';
import { selectCurrentUser } from '@store/auth/authSelectors';
import { ClientOutstandingOrder } from '@/renderer/services/accommodationService';
import {
  buildBillItems,
  resolveBillClientDetails,
  resolveBillPrinter,
} from '@/renderer/services/billPrintingService';
import { settleCreditBill } from '@/renderer/services/orderService';
import { printBillTicket } from '@/renderer/services/printerService';
import { fetchReportOrderDetails } from '@/renderer/services/reportsService';
import type { SalesReportOrderDetails } from '@/renderer/types/report';
import { isElectron } from '@/renderer/util';
import { apiError, apiSuccess } from '@/renderer/util/notify';
import { Loader2, Wallet, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

const REAL_PAYMENT_TYPES = new Set([
  'cash',
  'card',
  'mobile_money',
  'bank',
  'check',
]);

const formatAmount = (value: number): string =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

interface SettleCreditBillModalProps {
  isOpen: boolean;
  order: ClientOutstandingOrder | null;
  onClose: () => void;
  onSettled: () => void;
}

export const SettleCreditBillModal: React.FC<SettleCreditBillModalProps> = ({
  isOpen,
  order,
  onClose,
  onSettled,
}) => {
  const { data: allPaymentMethods = [], isLoading: isLoadingMethods } =
    usePaymentMethods();
  const currentUser = useAppSelector(selectCurrentUser);

  const paymentMethods = useMemo(
    () => allPaymentMethods.filter((pm) => REAL_PAYMENT_TYPES.has(pm.type)),
    [allPaymentMethods],
  );

  const [selectedMethodId, setSelectedMethodId] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingReceiptDetails, setPendingReceiptDetails] =
    useState<SalesReportOrderDetails | null>(null);
  const [isPrintingReceipt, setIsPrintingReceipt] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setNotes('');
      setIsSubmitting(false);
      setPendingReceiptDetails(null);
      setIsPrintingReceipt(false);
      return;
    }
    if (paymentMethods.length > 0) {
      setSelectedMethodId(String(paymentMethods[0].id));
    }
  }, [isOpen, paymentMethods]);

  const handleSubmit = async () => {
    if (!order || !selectedMethodId) return;

    setIsSubmitting(true);
    const result = await settleCreditBill(
      order.order_id,
      Number(selectedMethodId),
      notes.trim() || undefined,
    );
    setIsSubmitting(false);

    if (!result.success) {
      apiError(result.error || 'Failed to settle credit bill');
      return;
    }

    apiSuccess(`Credit bill #${order.bill_number} settled successfully.`);

    // Offer to print a receipt for the now-settled bill. If the just-settled
    // order's details can't be fetched (network hiccup right after settling
    // — this call is Supabase-only), fall back to the previous behavior and
    // just close, since a receipt is a courtesy, not a required step.
    const detailsResponse = await fetchReportOrderDetails(order.order_id, true);
    if (detailsResponse.success && detailsResponse.data) {
      setPendingReceiptDetails(detailsResponse.data.data);
      return;
    }

    onSettled();
    onClose();
  };

  const handleConfirmPrintReceipt = async () => {
    if (!pendingReceiptDetails) return;

    if (!isElectron()) {
      apiError('Printing is only available in the desktop app.');
      setPendingReceiptDetails(null);
      onSettled();
      onClose();
      return;
    }

    setIsPrintingReceipt(true);
    try {
      const [printer, client] = await Promise.all([
        resolveBillPrinter(pendingReceiptDetails),
        resolveBillClientDetails(pendingReceiptDetails),
      ]);

      const isTakeAwayLikeOrder =
        pendingReceiptDetails.order.mode !== 'DINE' ||
        !pendingReceiptDetails.order.section_id;
      const fullName =
        `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim();
      const printedByName = fullName || currentUser?.email || 'System';

      const response = await printBillTicket({
        printer: {
          name: printer.name,
          connection_type: printer.connection_type,
          printer_identifier: printer.printer_identifier,
          ip_address: printer.ip_address,
          port: printer.port,
          paper_size: printer.paper_size,
          details: printer.details,
        },
        payload: {
          outlet_name: pendingReceiptDetails.order.outlet_name,
          bill_number: pendingReceiptDetails.order.bill_number,
          table_name:
            pendingReceiptDetails.order.table_name ||
            (isTakeAwayLikeOrder ? 'ORDER' : '-'),
          section_name:
            pendingReceiptDetails.order.section_name ||
            (isTakeAwayLikeOrder ? 'TAKEAWAY' : '-'),
          waiter_name: pendingReceiptDetails.order.waiter_name || '-',
          printed_by: printedByName,
          printed_at: new Date().toISOString(),
          is_receipt: true,
          items: buildBillItems(pendingReceiptDetails.items),
          client,
          total_amount: pendingReceiptDetails.order.total_amount,
          discount_amount: pendingReceiptDetails.order.discount_amount,
          net_amount: pendingReceiptDetails.order.net_amount,
          settlements: pendingReceiptDetails.settlements.map((settlement) => ({
            payment_method_name: settlement.payment_method_name,
            amount: settlement.amount,
          })),
        },
      });

      if (!response.success) {
        apiError(response.error || 'Failed to print receipt');
      } else {
        apiSuccess(
          `Receipt for Bill #${pendingReceiptDetails.order.bill_number} sent to ${printer.name}`,
        );
      }
    } catch (error) {
      apiError(
        error instanceof Error ? error.message : 'Failed to print receipt',
      );
    } finally {
      setIsPrintingReceipt(false);
      setPendingReceiptDetails(null);
      onSettled();
      onClose();
    }
  };

  const handleDeclinePrintReceipt = () => {
    setPendingReceiptDetails(null);
    onSettled();
    onClose();
  };

  if (!isOpen || !order) return null;

  const isDisabled =
    isSubmitting || isLoadingMethods || !selectedMethodId || order.amount <= 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
        onClick={isSubmitting ? undefined : onClose}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !isSubmitting) onClose();
        }}
        role="button"
        tabIndex={0}
        aria-label="Close modal"
      />

      <div className="relative w-full max-w-md overflow-hidden rounded-lg bg-white text-black shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Settle Credit Bill
            </h2>
            <p className="text-xs text-gray-500">
              Bill #{order.bill_number} &mdash; Credit from{' '}
              {formatDate(order.sale_date)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-60"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 px-6 pb-6 pt-5">
          {/* Summary card */}
          <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700 space-y-1">
            <p>
              Bill: <strong>#{order.bill_number}</strong>
            </p>
            {order.table_name && (
              <p>
                Table:{' '}
                <strong>
                  {order.table_name}
                  {order.section_name ? ` (${order.section_name})` : ''}
                </strong>
              </p>
            )}
            <p>
              Credit issued: <strong>{formatDate(order.sale_date)}</strong>
            </p>
            <p>
              Amount due:{' '}
              <strong className="text-emerald-700">
                {formatAmount(order.amount)}
              </strong>
            </p>
            {order.notes && (
              <p className="text-xs text-gray-500 italic">{order.notes}</p>
            )}
          </div>

          {/* Payment method */}
          <DropdownSelect
            label="Payment Method"
            value={selectedMethodId}
            onChange={setSelectedMethodId}
            options={paymentMethods.map((pm) => ({
              value: String(pm.id),
              label: pm.name,
            }))}
            placeholder={
              isLoadingMethods ? 'Loading payment methods…' : 'Select method'
            }
            isLoadingOptions={isLoadingMethods}
            disabled={isSubmitting || isLoadingMethods}
            searchable={paymentMethods.length > 5}
          />

          {/* Notes */}
          <div>
            <p className="mb-1 text-sm font-semibold text-gray-800">
              Additional Notes{' '}
              <span className="font-normal text-gray-500">(optional)</span>
            </p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={isSubmitting}
              rows={2}
              placeholder="Any additional notes about this payment"
              className="w-full resize-none rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
            />
            <p className="mt-1 text-xs text-gray-400">
              The settlement record will be auto-annotated with the original
              credit date.
            </p>
          </div>

          {/* Submit */}
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isDisabled}
            className="h-10 w-full text-xs font-semibold uppercase tracking-wide disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Processing…
              </>
            ) : (
              <>
                <Wallet size={14} />
                Record Payment — {formatAmount(order.amount)}
              </>
            )}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={Boolean(pendingReceiptDetails)}
        onClose={handleDeclinePrintReceipt}
        onConfirm={handleConfirmPrintReceipt}
        title="Print a receipt?"
        message={`Would you like to print a receipt for Bill #${pendingReceiptDetails?.order.bill_number ?? ''}?`}
        isLoading={isPrintingReceipt}
        confirmText="Print Receipt"
        cancelText="No, thanks"
      />
    </div>
  );
};
